import React, { useCallback, useMemo, useRef, useState } from 'react'

import { move } from '@dnd-kit/helpers'
import { DragDropProvider } from '@dnd-kit/react'
import { useSortable } from '@dnd-kit/react/sortable'
import {
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from 'react-hook-form'
import { GoGrabber } from 'react-icons/go'
import {
  HiCheck,
  HiExclamationTriangle,
  HiMiniPlus,
  HiOutlineTrash,
  HiXMark,
} from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'
import { v4 } from 'uuid'

import Button from '@/components/common/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/common/Dialog'
import IconButton from '@/components/common/IconButtonV2'
import Spinner from '@/components/common/Spinner'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import { type DragEvent } from '@/components/dnd/v2/KanbanBoard'
import TextInput from '@/components/form/TextInput'
import { ColorSelector } from '@/components/tags/ColorSelector'

import { getMaxStageIndex } from '@/helpers/pursuit'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { usePursuits } from '@/hooks/pursuits/usePursuits'
import { usePursuitStages } from '@/hooks/pursuits/usePursuitStages'
import { useUpsertPursuitStages } from '@/hooks/pursuits/useUpsertPursuitStages'

import type { UUID } from '@/types/Id'
import type { Pursuit } from '@/types/pursuit/Pursuit'
import type { PursuitStage, PursuitStageStatus } from '@/types/pursuit/Stage'

const WON_NAME_PATTERN = /\bwon\b|\bwin\b|\bwinner\b|\bwinning\b/i
const LOST_NAME_PATTERN = /\blost\b|\blose\b|\bloser\b|\blosing\b/i

interface EditableStage extends PursuitStage {
  isNew: boolean
}

interface EditStagesFormData {
  stages: EditableStage[]
}

interface EditStagesProps {
  stages: PursuitStage[]
  onCancel?: () => void
  onSuccess?: (data: PursuitStage[]) => void
  className?: string
}

interface StageItemProps {
  index: number
  stage: PursuitStage
  remove: (stage: PursuitStage) => void
}

const StageItem: React.FC<StageItemProps> = ({ stage, remove }) => {
  // TODO: this component is non-performant due to the dnd-kit caveat the EditStages component;
  // you can see below that we are resetting the value of the entire array every time the input
  // or color changes, which is very inefficient;

  const selectStagePursuits = useCallback(
    (pursuits: Pursuit[]) =>
      pursuits.filter((pursuit) => pursuit.stage.id === stage.id),
    [stage.id],
  )

  const { activeOrgId } = useActiveOrgId()
  const { data: pursuits } = usePursuits({
    orgId: activeOrgId,
    selector: selectStagePursuits,
  })

  const { setValue, watch } = useFormContext()

  const [inputIsFocused, setInputIsFocused] = useState(false)

  const stages = watch('stages')

  const currentName: string | undefined = useMemo(() => {
    return stages.find((s: any) => s.id === stage.id)?.name
  }, [stages, stage.id])

  const currentColor: PursuitStage['color'] | undefined = useMemo(() => {
    return stages.find((s: any) => s.id === stage.id)?.color ?? stage.color
  }, [stages, stage.id, stage.color])

  const currentStatusToApply: PursuitStageStatus | null = useMemo(() => {
    return stages.find((s: any) => s.id === stage.id)?.status_to_apply ?? null
  }, [stages, stage.id])

  const handleStatusToApplyChange = useCallback(
    (status: PursuitStageStatus) => {
      const isCurrentlySet = currentStatusToApply === status
      const updatedStages = stages.map((s: EditableStage) => {
        if (s.id === stage.id) {
          return { ...s, status_to_apply: isCurrentlySet ? null : status }
        }
        // Uncheck this status from any other stage (radio-style behavior)
        if (!isCurrentlySet && s.status_to_apply === status) {
          return { ...s, status_to_apply: null }
        }
        return s
      })
      setValue('stages', updatedStages)
    },
    [stages, stage.id, currentStatusToApply, setValue],
  )

  const removeDisabled = pursuits && pursuits.length > 0

  const { ref, isDragSource } = useSortable({
    id: stage.id,
    index: stage.index,
    type: 'item',
    group: 'stages',
    transition: {
      duration: 150,
      idle: false,
    },
    disabled: inputIsFocused,
  })

  const handleColorChange = useCallback(
    (color: PursuitStage['color']) => {
      const updatedStages = stages.map((s: PursuitStage) => {
        if (s.id === stage.id) {
          return { ...s, color }
        }
        return s
      })
      setValue('stages', updatedStages)
    },
    [stages, stage.id, setValue],
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newName = event.target.value
      let updatedStages = stages.map((s: PursuitStage) => {
        if (s.id === stage.id) {
          return { ...s, name: newName }
        }
        return s
      })

      // Auto-toggle won/lost based on stage name patterns
      const nameTrimmed = newName.trim()
      const hasWonMatch =
        WON_NAME_PATTERN.test(nameTrimmed) &&
        !LOST_NAME_PATTERN.test(nameTrimmed)
      const hasLostMatch =
        LOST_NAME_PATTERN.test(nameTrimmed) &&
        !WON_NAME_PATTERN.test(nameTrimmed)

      const noOtherWonStage = !updatedStages.some(
        (s: EditableStage) => s.id !== stage.id && s.status_to_apply === 'won',
      )
      const noOtherLostStage = !updatedStages.some(
        (s: EditableStage) => s.id !== stage.id && s.status_to_apply === 'lost',
      )

      if (hasWonMatch && noOtherWonStage) {
        updatedStages = updatedStages.map((s: EditableStage) => ({
          ...s,
          status_to_apply:
            s.id === stage.id
              ? 'won'
              : s.status_to_apply === 'won'
                ? null
                : s.status_to_apply,
        }))
      } else if (hasLostMatch && noOtherLostStage) {
        updatedStages = updatedStages.map((s: EditableStage) => ({
          ...s,
          status_to_apply:
            s.id === stage.id
              ? 'lost'
              : s.status_to_apply === 'lost'
                ? null
                : s.status_to_apply,
        }))
      }

      setValue('stages', updatedStages)
    },
    [stages, stage.id, setValue],
  )

  const handleInputFocus = useCallback(() => {
    setInputIsFocused(true)
  }, [setInputIsFocused])

  const handleInputBlur = useCallback(() => {
    setInputIsFocused(false)
  }, [setInputIsFocused])

  const handleRemove = useCallback(() => {
    if (removeDisabled) return
    remove(stage)
  }, [removeDisabled, remove, stage])

  const handleSetAsToWon = useCallback(() => {
    handleStatusToApplyChange('won')
  }, [handleStatusToApplyChange])

  const handleSetAsToLost = useCallback(() => {
    handleStatusToApplyChange('lost')
  }, [handleStatusToApplyChange])

  return (
    <div
      ref={ref}
      data-dragging={isDragSource}
      className={twMerge(
        'relative flex items-center justify-between w-full p-2 gap-[10px] border border-transparent rounded-md bg-white dark:bg-default-dark',
        'transition-colors duration-150',
        'border border-primary-200 dark:border-primary-800',
      )}
    >
      <div className="flex flex-1 items-center gap-[6px]">
        <div className="flex items-center justify-center">
          <GoGrabber
            className="w-4 h-4 stroke-[1px] text-primary-700 dark:text-primary-300"
            onMouseDown={handleInputBlur}
          />
        </div>
        <TextInput
          value={currentName}
          className={twMerge('h-7 px-[6px] rounded-[4px] text-sn')}
          containerClassName="w-1/2"
          placeholder="Stage name"
          autoComplete="off"
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          maxLength={24}
        />
        {currentColor && (
          <div className="flex items-center justify-center">
            <ColorSelector
              color={currentColor}
              onChange={handleColorChange}
              triggerProps={{ tooltip: 'Change color' }}
            />
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={handleSetAsToWon}
              className={twMerge(
                'flex items-center justify-center gap-1 w-[60px] py-0.5 rounded-full text-xs font-medium border transition-colors cursor-pointer select-none',
                currentStatusToApply === 'won'
                  ? 'bg-green-100 dark:bg-green-900/40 border-green-300 dark:border-green-600 text-green-700 dark:text-green-300'
                  : 'bg-transparent border-primary-300 dark:border-primary-600 text-primary-500 dark:text-primary-400 hover:border-green-300 hover:text-green-600 dark:hover:border-green-600 dark:hover:text-green-400',
              )}
            >
              {currentStatusToApply === 'won' && (
                <HiCheck className="w-3 h-3" />
              )}
              Won
            </button>
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-[240px]">
            Pursuits moved to this stage will automatically be marked as Won and
            considered closed
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={handleSetAsToLost}
              className={twMerge(
                'flex items-center justify-center gap-1 w-[60px] py-0.5 rounded-full text-xs font-medium border transition-colors cursor-pointer select-none',
                currentStatusToApply === 'lost'
                  ? 'bg-red-100 dark:bg-red-900/40 border-red-300 dark:border-red-600 text-red-700 dark:text-red-300'
                  : 'bg-transparent border-primary-300 dark:border-primary-600 text-primary-500 dark:text-primary-400 hover:border-red-300 hover:text-red-600 dark:hover:border-red-600 dark:hover:text-red-400',
              )}
            >
              {currentStatusToApply === 'lost' && (
                <HiXMark className="w-3 h-3" />
              )}
              Lost
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-[240px]">
            Pursuits moved to this stage will automatically be marked as Lost
            and considered closed
          </TooltipContent>
        </Tooltip>
        <IconButton
          type="button"
          onClick={handleRemove}
          tooltip={
            removeDisabled
              ? 'Cannot remove stage with active pursuits'
              : 'Remove stage'
          }
          className={
            removeDisabled
              ? ''
              : 'hover:bg-primary-200 dark:hover:bg-primary-800'
          }
          disabled={removeDisabled}
          tooltipContentProps={{
            side: 'left',
          }}
        >
          <HiOutlineTrash className="w-4 h-4 text-red-600 dark:text-red-500" />
        </IconButton>
      </div>
    </div>
  )
}

export const EditStages: React.FC<EditStagesProps> = ({
  stages,
  onCancel,
  onSuccess,
  className,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { mutateAsync: upsertStages, isPending: isUpsertingStages } =
    useUpsertPursuitStages({
      orgId: activeOrgId,
    })

  const methods = useForm<EditStagesFormData>({
    defaultValues: {
      stages: stages
        .sort((a, b) => a.index - b.index)
        .map((stage) => ({
          ...stage,
          isNew: false,
        })),
    },
    resolver: (values) => {
      const errors: any = {}

      if (values.stages.length === 0) {
        errors.stages = {
          type: 'required',
          message: 'At least one stage is required',
        }
      }

      // Check for duplicate names
      const names = values.stages.map((stage) =>
        stage.name.trim().toLowerCase(),
      )
      const duplicates = names.filter(
        (name, index) => name && names.indexOf(name) !== index,
      )

      if (duplicates.length > 0) {
        errors.stages = {
          type: 'unique',
          message: 'Stage names must be unique',
        }
      }

      // Check for empty names
      let hasEmptyName = false
      values.stages.forEach((stage, index) => {
        if (!stage.name.trim()) {
          hasEmptyName = true
        }
      })

      if (hasEmptyName) {
        errors.stages = {
          type: 'required',
          message: 'Stage names cannot be empty',
        }
      }

      return {
        values,
        errors,
      }
    },
  })

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    watch,
  } = methods

  const { fields, append } = useFieldArray({
    control,
    name: 'stages',
    keyName: 'customId',
  })

  const formStages = watch('stages')

  // TODO: ideally we use the form stages/useFieldArray api to set the stage order,
  // but dnd-kit does not play nicely with setting the index of a field
  // via form rather than via state
  const [stageOrder, setStageOrder] = useState<PursuitStage[]>(
    stages.sort((a, b) => a.index - b.index),
  )
  // The stages this form started from, so a save over someone else's changes is refused.
  const expectedStageIds = useRef(stages.map((stage) => stage.id))

  const onSubmit = useCallback(
    async (data: EditStagesFormData) => {
      const stageOrderMap = new Map(
        stageOrder.map((stage, index) => [stage.id, index]),
      )
      const orderedStages = [...data.stages].sort(
        (a, b) => stageOrderMap.get(a.id)! - stageOrderMap.get(b.id)!,
      )

      try {
        const newStages = await upsertStages({
          stages: orderedStages.map((stage, index) => ({
            id: stage.isNew ? undefined : stage.id,
            name: stage.name,
            color: stage.color,
            index,
            status_to_apply: stage.status_to_apply,
          })),
          expected_stage_ids: expectedStageIds.current,
        })
        expectedStageIds.current = newStages.map((stage) => stage.id)
        setStageOrder(newStages.sort((a, b) => a.index - b.index))
        setValue(
          'stages',
          newStages.map((stage) => ({
            ...stage,
            isNew: false,
          })),
        )
        onSuccess?.(newStages)
      } catch (error) {
        // Keep the user's edits so they can retry.
        console.error('Error saving stages', error)
      }
    },
    [stageOrder, setStageOrder, upsertStages, setValue, onSuccess],
  )

  const handleAddNewStage = useCallback(() => {
    const id = v4() as UUID
    const index = getMaxStageIndex(fields) + 1
    // sort fields by stageOrder before appending to prevent
    // resetting to previous index state
    const newStage: EditableStage = {
      id,
      name: '',
      color: 'zinc',
      index,
      isNew: true,
      created_at: new Date().toISOString(),
      last_modified: new Date().toISOString(),
      organization_id: activeOrgId as UUID,
      status_to_apply: null,
    }
    append(newStage)
    setStageOrder([...stageOrder, newStage])
  }, [fields, stageOrder, append, setStageOrder])

  const handleCancel = useCallback(() => {
    onCancel?.()
    reset()
  }, [onCancel, reset])

  const handleRemove = useCallback(
    (stage: PursuitStage) => {
      setValue(
        'stages',
        formStages.filter((f) => f.id !== stage.id),
      )
      setStageOrder(stageOrder.filter((s) => s.id !== stage.id))
    },
    [formStages, stageOrder, setValue, setStageOrder],
  )

  const handleDragOver = useCallback(
    (event: DragEvent) => {
      const { source, target } = event.operation
      if (!source || !target || source.id === target.id) return
      const result = move(stageOrder, source, target)
      setStageOrder(result)
    },
    [stageOrder, setStageOrder],
  )

  const missingStagesBanner = useMemo(() => {
    const hasWon = formStages.some(
      (s: EditableStage) => s.status_to_apply === 'won',
    )
    const hasLost = formStages.some(
      (s: EditableStage) => s.status_to_apply === 'lost',
    )
    if (hasWon && hasLost) return null

    const bothMissing = !hasWon && !hasLost
    const label = bothMissing ? 'Won and Lost' : !hasWon ? 'Won' : 'Lost'

    return { bothMissing, label }
  }, [formStages])

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={twMerge('flex flex-col gap-[6px]', className)}
      >
        {missingStagesBanner && (
          <div className="flex items-start gap-2 p-2 mb-1 rounded-md bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 text-sm">
            <HiExclamationTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              You&apos;re missing a {missingStagesBanner.label} stage. Without{' '}
              {missingStagesBanner.bothMissing ? 'them' : 'it'}, Sweetspot
              won&apos;t be able to track pursuits as won or lost.
            </span>
          </div>
        )}
        {/* @ts-ignore */}
        <DragDropProvider onDragOver={handleDragOver}>
          {stageOrder.map((stage, index) => {
            return (
              <StageItem
                key={stage.id}
                index={index}
                stage={stage}
                remove={handleRemove}
              />
            )
          })}
        </DragDropProvider>
        <div className="flex items-center justify-between pt-4 gap-[6px]">
          <Button
            type="button"
            variant="outline-compact"
            disabled={isUpsertingStages}
            onClick={handleAddNewStage}
          >
            <HiMiniPlus className="w-4 h-4 text-primary-700 dark:text-primary-300" />
            <span>Add New Stage</span>
          </Button>
          <div className="flex gap-[6px]">
            <Button
              type="button"
              variant="outline-compact"
              onClick={handleCancel}
              disabled={isUpsertingStages}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary-compact"
              disabled={isUpsertingStages}
              isLoading={isUpsertingStages}
            >
              Save Changes
            </Button>
          </div>
        </div>
        {errors.stages && (
          <div className="flex justify-end w-full text-red-600 dark:text-red-500 text-[13px]">
            {errors.stages.message}
          </div>
        )}
      </form>
    </FormProvider>
  )
}

interface EditStagesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: (data: PursuitStage[]) => void
  onCancel?: () => void
}

export const EditStagesDialog: React.FC<EditStagesDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
  onCancel,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { data: pursuitStages } = usePursuitStages({
    orgId: activeOrgId,
  })

  const handleOnSuccess = (data: PursuitStage[]) => {
    onSuccess?.(data)
    onOpenChange(false)
  }

  const handleOnCancel = () => {
    onCancel?.()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-full max-w-3xl space-y-4"
        closeButtonClassName="top-0 right-3"
      >
        <DialogHeader>
          <DialogTitle>Edit Stages</DialogTitle>
          <DialogDescription>
            Customize the stages that your team uses to track pursuit progress.
            Add new stages, reorder them, or update their names and colors. Mark
            a stage as Won or Lost to automatically close pursuits that reach
            it. Changes apply to all pursuits in your organization.
          </DialogDescription>
        </DialogHeader>
        {pursuitStages ? (
          <EditStages
            stages={pursuitStages}
            onSuccess={handleOnSuccess}
            onCancel={handleOnCancel}
            className="bg-primary-50 dark:bg-primary-900 p-3 rounded-md"
          />
        ) : (
          <div className="flex justify-center items-center h-full">
            <Spinner />
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
